import { LoadingSpinner } from '@slal/ui/animations';
import { Alert } from '@slal/ui/display';
import { P } from '@slal/ui/typography';
import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { DocumentType, useFormSession, UserContextState, useRestApiPostRequest, UseUserContextResponse } from '~/hooks';
import { CustomerSetupResponse, FormDataType, FormKeys } from '~/types';
import { UserContext } from './UserContext';

export const USER_CONTEXT_STATE_KEY = 'USER_CONTEXT_STATE_KEY';
const getUserContextState = (): UserContextState =>
    (sessionStorage.getItem(USER_CONTEXT_STATE_KEY) as UserContextState) || UserContextState.INIT;
const setUserContextState = (state: UserContextState) => sessionStorage.setItem(USER_CONTEXT_STATE_KEY, state);
const removeUserContextState = () => sessionStorage.removeItem(USER_CONTEXT_STATE_KEY);

const handleRedirect = (redirectUrl?: string, reason?: string) => {
    if (!redirectUrl) {
        console.warn('⚠️ Redirect URL is missing. No redirection will occur.');
        return;
    }

    console.info(`🔄 Redirecting user: ${reason}`, redirectUrl);

    if (window.location.href !== redirectUrl) {
        window.location.href = redirectUrl;
    }
};

// Define a type that extends DocumentType for the request
type CustomerSetupRequest = DocumentType;

export const UserContextProvider = ({ children }: PropsWithChildren) => {
    const { getSession, setSession } = useFormSession();

    const reset = useCallback(() => {
        removeUserContextState();
    }, []);

    const [userContext, setUserContext] = useState<UseUserContextResponse>({
        data: undefined,
        error: undefined,
        loading: false,
        reset,
    });

    const { data, loading, error, status, sendRequest } = useRestApiPostRequest<
        CustomerSetupResponse,
        CustomerSetupRequest
    >({
        path: '/secure/customer-setup',
        options: { withCredentials: true },
    });

    useEffect(() => {
        const currentState = getUserContextState();

        if (currentState === UserContextState.COMPLETE) {
            const session = getSession(FormKeys.CUSTOMER_DETAILS);
            const customerDetails = session?.customerDetails || {};
            setUserContext(prev => ({
                ...prev,
                data: customerDetails,
                loading: false,
            }));
        } else if (currentState === UserContextState.INIT || currentState === UserContextState.ERROR) {
            setUserContextState(UserContextState.LOADING);
            setUserContext(prev => ({
                ...prev,
                loading: true,
            }));
            sendRequest();
        }
    }, []);

    useEffect(() => {
        if (data?.redirectUrl) {
            handleRedirect(data.redirectUrl, 'Via JSON response');
            return;
        }

        if (status === 401 && error?.response?.data?.redirectUrl) {
            handleRedirect(error.response.data.redirectUrl, 'Token expired');
        }
    }, [data, status, error]);

    useEffect(() => {
        if (loading || (!data && !error)) {
            return;
        }

        if (data) {
            setUserContextState(UserContextState.COMPLETE);
            const { customerDetails } = data;
            setSession({ customerDetails } as Partial<FormDataType>);
            setUserContext(prev => ({
                ...prev,
                data: customerDetails,
                loading: false,
            }));
        } else if (error) {
            setUserContextState(UserContextState.ERROR);
            setUserContext(prev => ({
                ...prev,
                data: undefined,
                loading: false,
                error:
                    status === 403
                        ? 'Sorry, you are ineligible to use this tool'
                        : 'There was an error retrieving your customer details. Please refresh the page or try again later',
            }));
        }
    }, [data, error, loading, setSession, getSession, status]);

    return (
        <UserContext.Provider value={userContext}>
            {loading && <LoadingSpinner content="Loading your customer details" />}
            {error && (
                <Alert variant="warning" icon={true}>
                    <P>{userContext.error}</P>
                </Alert>
            )}

            {!loading && !error && children}
        </UserContext.Provider>
    );
};

export default UserContextProvider;
